import type { UseQueryOptions } from '@core/types'
import type { UseGameWinnersSearchResponse } from '@game/types'
import type { MaybeRefOrGetter } from 'vue'
import { computed, toValue, useApi, useCoreConfig, useQuery } from '#imports'

export interface UseGameWinnersSearchOptions extends Pick<UseQueryOptions<UseGameWinnersSearchResponse>, 'enabled'> {
    /**
     * How to sort results.
     *
     * - `amount`: The largest results are first.
     * - `time`: The newest results are first.
     */
    sortBy: MaybeRefOrGetter<'amount' | 'time'>
    gameSlug?: MaybeRefOrGetter<string | undefined>
}

export const useGameWinnersSearch = (options: UseGameWinnersSearchOptions) => {
    const { sortBy, enabled = true, gameSlug = undefined } = options

    const { site } = useCoreConfig()
    const api = useApi({
        service: 'monolith'
    })

    const key = () => {
        const base = `game:winners-search:${toValue(sortBy)}`

        const gameSlugRaw = toValue(gameSlug)

        if (!gameSlugRaw) {
            return base
        }

        return `${base}:${gameSlugRaw}`
    }

    const endpoint = computed(() => {
        const base = `/pages/get_winners/${toValue(sortBy)}/`

        const gameSlugRaw = toValue(gameSlug)

        if (!gameSlugRaw) return base

        return `${base}${gameSlugRaw}`
    })

    const query = useQuery(async () => {
        const data = await api<UseGameWinnersSearchResponse>(endpoint.value, {
            query: {
                brand: site.brand
            }
        })

        // Add id to winners
        const winners = data.winners?.map((winner) => {
            return {
                ...winner,
                id: `${winner.wonBy}-${winner.wonAt}`,
            }
        }) ?? []

        return {
            ...data,
            winners
        }
    }, {
        key,
        enabled,
        lazy: true,
        server: false
    })

    return query
}
